/* eslint-disable react-refresh/only-export-components -- Context files legitimately mix component and non-component exports */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
  type ReactNode,
} from "react";
import type { GmailThread } from "../lib/gmail";

// ── Payload types ────────────────────────────────────────────────────────────

export type UpgradeReason = "task_limit" | "general";

export type DeleteConfirmPayload = {
  type: "list" | "tag" | "task" | "subtask";
  name: string;
  id?: string;
  taskCount: number;
  listBreakdown?: { open: number; completed: number; archived: number };
};

export type TaskPickerPayload = {
  threads: GmailThread[];
  mode: "link" | "subtask";
};

// ── Dialog id → payload map ──────────────────────────────────────────────────

// Single source of truth: each dialog id maps to the exact payload it expects
// (or `void` if it has none). `open`/`replace`/`payloadFor` derive their types
// from this map, so payloads are checked end-to-end with no casts at call sites.
export type DialogPayloads = {
  "add-task": string[] | null; // source message ids (for email → task)
  "task-picker": TaskPickerPayload;
  "add-list": void;
  "add-tag": void;
  "settings": void;
  "filter": void;
  "reorder-lists": void;
  "search": void;
  "shortcuts": void;
  "shortcut-hints": void;
  "upgrade": UpgradeReason;
  "voice-review": void;
  "delete-confirm": DeleteConfirmPayload;
};

export type DialogId = keyof DialogPayloads;

// Variadic-rest tuple: payload arg is required when the payload type isn't
// void, omitted when it is. Lets `dialog.open("add-list")` and
// `dialog.open("upgrade", "task_limit")` share one signature.
type PayloadArgs<K extends DialogId> = DialogPayloads[K] extends void
  ? []
  : [DialogPayloads[K]];

// ── Reducer ──────────────────────────────────────────────────────────────────

// Dialogs are modeled as a stack. Most flows keep the stack at a single entry
// (opening a dialog from the board pushes onto an empty stack), so the default
// behavior is still "one dialog at a time". But some confirmations legitimately
// stack on top of their parent — e.g. the delete-list/delete-tag confirm
// rendered over the still-open Settings dialog, or the shortcuts reference
// summoned with "?" while another dialog is open. `open` pushes, `close` pops
// the top, `replace` swaps the top (used when one dialog supersedes another,
// e.g. Settings → Add List).
type DialogEntry = {
  [K in DialogId]: { id: K; payload: DialogPayloads[K] };
}[DialogId];

type DialogState = {
  stack: DialogEntry[];
};

type DialogAction =
  | { type: "OPEN"; entry: DialogEntry }
  | { type: "CLOSE" }
  | { type: "REPLACE"; entry: DialogEntry };

function dialogReducer(state: DialogState, action: DialogAction): DialogState {
  switch (action.type) {
    case "OPEN": {
      const top = state.stack[state.stack.length - 1];
      // Re-opening the dialog that's already on top refreshes its payload
      // instead of stacking a duplicate — keeps repeated triggers (e.g.
      // pressing "?" twice) idempotent, matching the old boolean behavior.
      if (top && top.id === action.entry.id) {
        return { stack: [...state.stack.slice(0, -1), action.entry] };
      }
      return { stack: [...state.stack, action.entry] };
    }
    case "CLOSE":
      return { stack: state.stack.slice(0, -1) };
    case "REPLACE":
      // Swap the top entry; if the stack is empty, behaves like OPEN.
      return { stack: [...state.stack.slice(0, -1), action.entry] };
    default:
      return state;
  }
}

function makeEntry<K extends DialogId>(
  id: K,
  args: PayloadArgs<K>,
): DialogEntry {
  // `args[0]` is the payload when one is required and `undefined` otherwise.
  // The cast to DialogEntry is safe because the generic K threads the payload
  // type from the call site through PayloadArgs.
  return { id, payload: args[0] as DialogPayloads[K] } as DialogEntry;
}

// ── Context ──────────────────────────────────────────────────────────────────

export interface DialogContextValue {
  /** Id of the top-most (front-most) dialog, or null if none are open. */
  current: DialogId | null;
  open: <K extends DialogId>(id: K, ...args: PayloadArgs<K>) => void;
  close: () => void;
  replace: <K extends DialogId>(id: K, ...args: PayloadArgs<K>) => void;
  /** True if `id` is anywhere in the stack (so stacked parents stay rendered). */
  isOpen: (id: DialogId) => boolean;
  /**
   * Typed payload reader. Returns the payload of the *top-most* entry matching
   * `id`, or undefined if `id` isn't open. The return type follows from `id`,
   * so consumers don't cast.
   */
  payloadFor: <K extends DialogId>(id: K) => DialogPayloads[K] | undefined;
}

const DialogContext = createContext<DialogContextValue | null>(null);

// ── Provider ─────────────────────────────────────────────────────────────────

export function DialogProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(dialogReducer, { stack: [] });

  // Stable dispatch wrappers — dispatch itself is guaranteed stable by React
  const open = useCallback(
    <K extends DialogId>(id: K, ...args: PayloadArgs<K>) =>
      dispatch({ type: "OPEN", entry: makeEntry(id, args) }),
    [],
  );
  const close = useCallback(() => dispatch({ type: "CLOSE" }), []);
  const replace = useCallback(
    <K extends DialogId>(id: K, ...args: PayloadArgs<K>) =>
      dispatch({ type: "REPLACE", entry: makeEntry(id, args) }),
    [],
  );

  const value = useMemo<DialogContextValue>(() => {
    const top = state.stack[state.stack.length - 1];
    return {
      current: top ? top.id : null,
      open,
      close,
      replace,
      isOpen: (id) => state.stack.some((entry) => entry.id === id),
      payloadFor: <K extends DialogId>(id: K) => {
        // Top-most match wins — relevant only if the same id appears twice
        // in the stack, which the OPEN idempotency rule already prevents.
        for (let i = state.stack.length - 1; i >= 0; i--) {
          const entry = state.stack[i];
          if (entry.id === id) return entry.payload as DialogPayloads[K];
        }
        return undefined;
      },
    };
  }, [state, open, close, replace]);

  return (
    <DialogContext.Provider value={value}>{children}</DialogContext.Provider>
  );
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useDialog(): DialogContextValue {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error("useDialog must be used inside DialogProvider");
  return ctx;
}
